import React from "react";
import { ArrowDownNarrowWide, ArrowUpNarrowWide, Columns3, EyeOff, Search } from "lucide-react";
import {
  getSavedViewSummary,
  type FieldMap,
} from "../../../UniversalCRMTable/components/SimpleTable/filterDisplayUtils";
import type { SavedViewConfig } from "../../../UniversalCRMTable/types";

interface SavedViewConfigPreviewProps {
  view: SavedViewConfig;
  fields: FieldMap;
  /** When provided, renders a "Hide from bar" action in the footer. */
  onHide?: () => void;
}

const VISIBILITY_LABEL: Record<string, string> = {
  private: "Private",
  shared: "Shared",
  public: "Public",
};

/**
 * Read-only breakdown of a saved view's filter configuration: search, each
 * active filter and its values, advanced-filter count, sort, and visible-column
 * count. Rendered inside the view pill's hover/right-click popover so a user can
 * answer "what exactly is this view filtering?" without opening an editor.
 */
export const SavedViewConfigPreview: React.FC<SavedViewConfigPreviewProps> = ({
  view,
  fields,
  onHide,
}) => {
  const summary = getSavedViewSummary(view, fields);
  const visibility = view.visibility ? VISIBILITY_LABEL[view.visibility] : undefined;

  return (
    <div className="text-xs">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">{view.name}</span>
        {visibility && (
          <span className="shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-2xs font-medium uppercase tracking-wide text-gray-500">
            {visibility}
          </span>
        )}
      </div>

      <div className="p-3 space-y-2.5 max-h-72 overflow-y-auto">
        {summary.isEmpty && (
          <p className="text-muted-foreground">No filters — shows all records.</p>
        )}

        {summary.search && (
          <Row icon={<Search className="h-3 w-3" />} label="Search">
            <span className="text-gray-800 dark:text-gray-200 font-medium">{summary.search}</span>
          </Row>
        )}

        {summary.filterEntries.map((entry, i) => (
          <Row key={`${entry.label}-${i}`} label={entry.label}>
            <div className="flex flex-wrap gap-1">
              {entry.parts.items.map((item, j) => (
                <span
                  key={`${item}-${j}`}
                  className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-2xs font-medium text-gray-800 dark:text-gray-200"
                >
                  {item}
                </span>
              ))}
            </div>
          </Row>
        ))}

        {summary.advancedCount > 0 && (
          <Row label="Advanced filters">
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {summary.advancedCount} condition{summary.advancedCount === 1 ? "" : "s"}
            </span>
          </Row>
        )}

        {summary.sort && (
          <Row
            icon={
              summary.sort.direction === "asc" ? (
                <ArrowUpNarrowWide className="h-3 w-3" />
              ) : (
                <ArrowDownNarrowWide className="h-3 w-3" />
              )
            }
            label="Sorted by"
          >
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {summary.sort.label} ({summary.sort.direction === "asc" ? "ascending" : "descending"})
            </span>
          </Row>
        )}

        {typeof summary.visibleColumnsCount === "number" && (
          <Row icon={<Columns3 className="h-3 w-3" />} label="Columns">
            <span className="text-gray-800 dark:text-gray-200 font-medium">
              {summary.visibleColumnsCount} visible
            </span>
          </Row>
        )}
      </div>

      {onHide && (
        <div className="border-t p-2">
          <button
            type="button"
            onClick={onHide}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-2xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <EyeOff className="h-3.5 w-3.5" />
            Hide from bar
          </button>
        </div>
      )}
    </div>
  );
};

function Row({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}
